/**
 * Deny Request Modal Component
 *
 * Modal dialog for denying access requests with required review notes
 */

import React, { useState } from 'react';
import type { AccessRequest } from '../../../types/access-control';

interface DenyRequestModalProps {
  request: AccessRequest;
  onConfirm: (reviewNotes: string) => Promise<void>;
  onCancel: () => void;
}

export const DenyRequestModal: React.FC<DenyRequestModalProps> = ({
  request,
  onConfirm,
  onCancel,
}) => {
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reviewNotes.trim()) {
      setError('Review notes are required when denying a request');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onConfirm(reviewNotes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny request');
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Deny Access Request
          </h2>

          <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
            <p className="text-sm font-medium text-gray-700">
              {request.name || 'Unknown User'}
            </p>
            <p className="text-xs text-gray-600">{request.email}</p>
            {request.reason && (
              <p className="text-xs text-gray-500 mt-2">
                <span className="font-medium">Reason: </span>
                {request.reason}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="reviewNotes"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Review Notes <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="reviewNotes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Explain why this request is being denied..."
                  disabled={isSubmitting}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Required: Please provide a reason for denying this request
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Denying...' : 'Deny Request'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
